/**
 * PreviewLabPage — /preview-lab
 * ─────────────────────────────────────────────────────────────────
 * Feb 27 2026 · After Michael picked "Option A · Signature Motif",
 * this page now previews the FULL SET of six doorway motifs the
 * way they render on the live OML "Six doorways" section, so he
 * can review the family before we send traffic there.
 * ────────────────────────────────────────────────────────────── */
import React from "react";
import {
  ReclaimMotif,
  AtelierMotif,
  SoulumoMotif,
  AbsorptionMotif,
  FoundationsMotif,
  DailyMotif,
} from "./OneMillionLights/DoorwayMotifs";

const TOK = {
  ink: "#050a17",
  bg: "radial-gradient(ellipse at 50% 20%, #131f38 0%, #0a132a 40%, #030710 85%)",
  cream: "#f5ecd9",
  creamReadable: "rgba(245,236,217,0.98)",
  creamDim: "rgba(245,236,217,0.86)",
  creamMist: "rgba(245,236,217,0.66)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.98)",
  goldMist: "rgba(224,169,122,0.34)",
};

const DOORWAYS = [
  { id: "reclaim",     n: "01", title: "Reclaim",             blurb: "A place to return to yourself — whatever your circumstances.", Motif: ReclaimMotif },
  { id: "atelier",     n: "02", title: "The Atelier",         blurb: "The transformational heart: a place to consciously re-architect the inner world.", Motif: AtelierMotif },
  { id: "soulumo",     n: "03", title: "Soulumo",             blurb: "Personal installations shaped through your words, voice, music, frequency and intention.", Motif: SoulumoMotif },
  { id: "absorption",  n: "04", title: "The Absorption Engine", blurb: "A calmer way to help knowledge become familiar rather than forcing it into memory.", Motif: AbsorptionMotif },
  { id: "foundations", n: "05", title: "Soulumo Foundations", blurb: "A quiet, curated collection for anyone finding their footing — Morning Light, Peace, Self-Belief, Rest.", Motif: FoundationsMotif },
  { id: "daily",       n: "06", title: "The Daily Routine",   blurb: "A simple daily rhythm of gratitude, activation, intention, immersion, reset and reflection. The first step is free.", Motif: DailyMotif },
];

function DoorwayCard({ d }) {
  const { Motif, n, title, blurb, id } = d;
  return (
    <div
      style={{
        background: "rgba(245,236,217,0.03)",
        border: `1px solid ${TOK.goldMist}`,
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
      }}
      data-testid={`preview-doorway-${id}`}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 10",
          overflow: "hidden",
          borderBottom: `1px solid ${TOK.goldMist}`,
        }}
      >
        <Motif />
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(5,10,23,0.45) 0%, transparent 40%, transparent 65%, rgba(5,10,23,0.45) 100%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 12,
            left: 14,
            fontSize: "0.55rem",
            letterSpacing: "0.38em",
            textTransform: "uppercase",
            color: TOK.goldSoft,
            fontFamily: '"Manrope", sans-serif',
          }}
        >
          Creation {n}
        </div>
      </div>
      <div style={{ padding: "20px 22px 22px" }}>
        <div
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "1.5rem",
            color: TOK.cream,
            fontWeight: 300,
            letterSpacing: "0.01em",
          }}
        >
          {title}
        </div>
        <p
          style={{
            marginTop: 10,
            fontStyle: "italic",
            fontSize: "0.95rem",
            lineHeight: 1.55,
            color: TOK.creamReadable,
            fontFamily: '"Cormorant Garamond", serif',
          }}
        >
          {blurb}
        </p>
      </div>
    </div>
  );
}

export default function PreviewLabPage() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: TOK.bg,
        color: TOK.cream,
        padding: "56px 20px 80px",
        fontFamily: '"Manrope", sans-serif',
      }}
      data-testid="preview-lab-page"
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div
            style={{
              fontSize: "0.55rem",
              letterSpacing: "0.4em",
              textTransform: "uppercase",
              color: TOK.goldSoft,
            }}
          >
            Preview Lab · Option A · Signature Motif
          </div>
          <h1
            style={{
              marginTop: 14,
              fontFamily: '"Cormorant Garamond", serif',
              fontWeight: 300,
              fontSize: "clamp(2rem, 5vw, 3rem)",
              color: TOK.cream,
            }}
          >
            Six doorways.{" "}
            <em style={{ color: TOK.gold }}>One open promise.</em>
          </h1>
          <p
            style={{
              marginTop: 14,
              maxWidth: 560,
              margin: "14px auto 0",
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              color: TOK.creamReadable,
              fontSize: "1rem",
              lineHeight: 1.6,
            }}
          >
            The full family of hand-crafted motifs. This is exactly how the
            live OML "Six doorways" section now renders — each doorway with
            its own gently-animating scene.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 22,
            alignItems: "start",
          }}
        >
          {DOORWAYS.map((d) => (
            <DoorwayCard key={d.id} d={d} />
          ))}
        </div>

        <div
          style={{
            marginTop: 56,
            padding: "22px 26px",
            background: "rgba(245,236,217,0.03)",
            border: `1px solid ${TOK.goldMist}`,
            borderRadius: 14,
            maxWidth: 720,
            margin: "56px auto 0",
          }}
        >
          <div
            style={{
              fontSize: "0.55rem",
              letterSpacing: "0.38em",
              textTransform: "uppercase",
              color: TOK.goldSoft,
              marginBottom: 10,
            }}
          >
            Live on OML
          </div>
          <p
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              color: TOK.creamReadable,
              lineHeight: 1.65,
              fontSize: "1rem",
            }}
          >
            The set is already wired into the "Six doorways" section on the
            OML landing page — scroll below the globe to see it in context.
            The top toolbar chips now fade away as you scroll, so the
            reading is uninterrupted.
          </p>
        </div>
      </div>
    </div>
  );
}
